import { useLayoutEffect, useRef } from 'react'
import AutoResizeTextarea from 'react-textarea-autosize'
import classNames from 'classnames'

import { USE_RESIZABLE_DIRECTION, useResizable } from '../../../hooks'
import { getQaSelector, isValueFunctionType } from '../../../utils/common'
import { ICON_COLOR, ICON_HEIGHT_16, IconNG } from '../../Icon'
import { TextareaPlaceholder } from '../TextareaPlaceholder'

import {
  DEFAULT_TEXTAREA_RESIZABLE_MAX_ROWS,
  DEFAULT_TEXTAREA_ROWS,
  QA_NAME_TEXTAREA_CONTEXT,
} from './TextareaElement.constants'

import * as styles from './TextareaElement.module.css'

import PullIcon from '../../../icons/textarea-more.svg'

function TextareaElement({
  autoResizeHeight,
  borderless,
  children,
  className,
  disabled,
  error,
  focused,
  hidePlaceholderOnFocus = true,
  maxLength,
  maxRows,
  minRows,
  name,
  placeholder,
  qaName,
  ref: forwardedRef,
  required,
  value,
  withoutManualResize,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
}) {
  const textareaRef = useRef(null)

  const { handleMouseDown, handleTouchStart, setResizableRef } = useResizable(
    USE_RESIZABLE_DIRECTION.Vertical
  )

  useLayoutEffect(() => {
    if (focused) {
      textareaRef.current?.focus()
    } else {
      textareaRef.current?.blur()
    }
  }, [focused])

  useLayoutEffect(() => {
    if (focused && textareaRef.current) {
      const caretPosition = textareaRef.current.value.length

      textareaRef.current.setSelectionRange(caretPosition, caretPosition)
    }
  }, [])

  function handleTextareaRef(node) {
    textareaRef.current = node
    setResizableRef(node)

    if (isValueFunctionType(forwardedRef)) {
      forwardedRef(node)
    } else if (forwardedRef) {
      forwardedRef.current = node
    }
  }

  function handleChange(event) {
    onChange?.(event.target.value)
  }

  const textareaBoxClassName = classNames(styles.textareaBox, {
    [styles.borderless]: borderless,
    [styles.disabled]: disabled,
    [styles.error]: !!error,
    [styles.focused]: focused,
    [styles.hidePlaceholderOnFocus]: hidePlaceholderOnFocus,
  })

  const textareaClassName = classNames(
    styles.textarea,
    {
      [styles.withoutManualResize]: autoResizeHeight || withoutManualResize,
    },
    className
  )

  const pullIconClassName = classNames(styles.textareaPullIcon, {
    [styles.disabled]: disabled,
  })

  const textareaQaProps = getQaSelector({
    context: QA_NAME_TEXTAREA_CONTEXT,
    defaultValue: QA_NAME_TEXTAREA_CONTEXT,
    value: qaName || name,
  })

  const textareaProps = {
    className: textareaClassName,
    disabled,
    maxLength,
    name,
    placeholder: placeholder || undefined,
    ref: handleTextareaRef,
    value,
    onBlur,
    onChange: handleChange,
    onFocus,
    onKeyDown,
    ...(autoResizeHeight
      ? {
          maxRows:
            maxRows && maxRows <= DEFAULT_TEXTAREA_RESIZABLE_MAX_ROWS
              ? maxRows
              : DEFAULT_TEXTAREA_RESIZABLE_MAX_ROWS,
          minRows,
        }
      : { rows: DEFAULT_TEXTAREA_ROWS }),
  }

  const Textarea = autoResizeHeight ? AutoResizeTextarea : 'textarea'

  return (
    <div {...textareaQaProps} className={textareaBoxClassName}>
      {disabled ? <div className={styles.disabledOverlay} /> : null}
      {autoResizeHeight || withoutManualResize ? null : (
        <IconNG
          Icon={PullIcon}
          className={pullIconClassName}
          color={ICON_COLOR.SecondaryTextColor70}
          size={ICON_HEIGHT_16}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        />
      )}
      {placeholder && !value ? (
        <TextareaPlaceholder
          boxClassName={styles.focusHider}
          placeholder={placeholder}
          required={required}
        />
      ) : null}
      <Textarea {...textareaProps} />
      {children}
    </div>
  )
}

export default TextareaElement
